import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import api from "@/lib/api";
import { VERTICAL_META, VerticalIcon } from "@/lib/verticals";
import ProviderCard from "@/components/ProviderCard";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { MagnifyingGlass, MapPin, Lightning } from "@phosphor-icons/react";

const HERO = "https://images.pexels.com/photos/13058812/pexels-photo-13058812.jpeg";

const Home = () => {
  const navigate = useNavigate();
  const [q, setQ] = useState("");
  const [providers, setProviders] = useState([]);
  const [verticals, setVerticals] = useState([]);

  useEffect(() => {
    api.get("/verticals").then((r) => setVerticals(r.data));
    api.get("/providers", { params: { sort: "rating" } }).then((r) => setProviders(r.data.slice(0, 6)));
  }, []);

  const go = (params) => {
    const sp = new URLSearchParams(params);
    navigate(`/search?${sp.toString()}`);
  };

  return (
    <div>
      {/* Hero */}
      <section className="relative">
        <div className="absolute inset-0">
          <img src={HERO} alt="" className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-br from-black/80 via-black/60 to-primary/40" />
        </div>
        <div className="relative max-w-6xl mx-auto px-4 md:px-6 py-20 md:py-28">
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }} className="max-w-2xl">
            <div className="inline-flex items-center gap-2 rounded-full bg-white/10 backdrop-blur px-3 py-1 text-white/90 text-sm mb-5 border border-white/20">
              <Lightning weight="fill" size={15} /> Prenota in 3 tocchi
            </div>
            <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-bold text-white leading-[1.05]">
              Il tuo prossimo servizio,<br />prenotato in un istante.
            </h1>
            <p className="text-white/80 text-lg mt-4 max-w-lg">
              Barbieri, beauty, manutentori e ristoranti. Trova i migliori professionisti vicino a te e prenota subito.
            </p>
            <div className="mt-8 flex flex-col sm:flex-row gap-3 max-w-xl">
              <div className="relative flex-1">
                <MagnifyingGlass className="absolute left-4 top-1/2 -translate-y-1/2 text-muted-foreground" size={18} />
                <Input value={q} onChange={(e) => setQ(e.target.value)}
                  onKeyDown={(e) => e.key === "Enter" && go({ q })}
                  data-testid="home-search-input"
                  placeholder="Cerca un servizio o professionista…"
                  className="pl-11 h-12 bg-white border-0 text-foreground rounded-full" />
              </div>
              <Button className="h-12 rounded-full px-8" data-testid="home-search-btn" onClick={() => go({ q })}>
                Cerca
              </Button>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Verticals */}
      <section className="max-w-6xl mx-auto px-4 md:px-6 py-12">
        <h2 className="font-heading text-2xl font-bold mb-5">Sfoglia per categoria</h2>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          {verticals.map((v, i) => (
            <motion.button key={v.id} data-testid={`vertical-${v.id}`}
              whileHover={{ y: -4 }} initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }}
              transition={{ delay: i * 0.06 }}
              onClick={() => go({ vertical: v.id })}
              className="flex flex-col items-start gap-3 p-5 rounded-2xl border border-border bg-card hover:border-primary hover:shadow-md transition-[border-color,box-shadow,transform]">
              <span className="grid place-items-center w-11 h-11 rounded-xl bg-accent text-accent-foreground">
                <VerticalIcon v={v.id} size={22} weight="duotone" />
              </span>
              <span className="font-heading font-semibold">{VERTICAL_META[v.id]?.label}</span>
            </motion.button>
          ))}
        </div>
      </section>

      {/* Top rated */}
      <section className="max-w-6xl mx-auto px-4 md:px-6 pb-16">
        <div className="flex items-center justify-between mb-5">
          <h2 className="font-heading text-2xl font-bold">Più votati vicino a te</h2>
          <Button variant="ghost" onClick={() => go({})} data-testid="see-all-btn">Vedi tutti</Button>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
          {providers.map((p, i) => <ProviderCard key={p.provider_id} p={p} index={i} />)}
        </div>
      </section>
    </div>
  );
};

export default Home;
